import { useCallback, useEffect, useMemo, useState } from 'react';

export const useClientWidthOptions = (options: number[], element: HTMLElement | null): number => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const opts = useMemo(() => options, []);

    const calculateOption = useCallback(() => {
        if (!element) {
            return 0;
        }

        const width = element?.getBoundingClientRect().width ?? 0;

        for (let i = 0; i < opts.length; i++) {
            if (width < opts[i]) {
                return i;
            }
        }

        return opts.length;
    }, [opts, element]);

    const [size, setSize] = useState(calculateOption());

    useEffect(() => {
        /*
         * we have to wait here because element size could be changed after render
         * and there is no other way to handle this situation
         */
        const to = setTimeout(() => {
            const newSize = calculateOption();

            if (newSize !== size) {
                setSize(newSize);
            }
        }, 0);

        return () => clearTimeout(to);
    });

    useEffect(() => {
        const handleResize = () => {
            setSize(calculateOption());
        };

        window.addEventListener('resize', handleResize);
        setSize(calculateOption());

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [calculateOption]);

    return size;
};
